import React, { useMemo, useState, useEffect, useContext } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { genericGet } from "../../services/apiServices";
import { GET_EXPORT_PROFORM_REPORT } from "../../services/apiRoutes";
import { AuthContext } from "../../context/AuthContext";
import CustomLoader from "../common/CustomLoader";
import CustomReport from "../common/CustomReport";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import { getColorById, getColorPlate, getCustumersList } from "../../services/CommonServices";
import { genericPost } from "../../services/authServices";
import CustomButton from "../common/CustomButton";
import CustomTextField from "../common/CustomTextField";
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomFilter from "../common/CustomFilter";
import { Box, Grid } from "@mui/material";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import CustomStatus from "../common/CustomStatus";

const title = "Proform Inv. Report";

const ProformInvReport = () => {
    const { t, i18n } = useTranslation();
    const companyDateFormat = useCompanyDateFormat();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [permission, setPermissions] = useState()
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [custumersList, setCustumersList] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [colorlist, setColorlist] = useState([]);

    const [filterPayload, setFilterPayload] = useState({
        shipmentId: '',
        customerNamesId: '',
        fromDate: '',
        toDate: '',

    });
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                shipmentId: '',
                customerNamesId: '',
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);
    const getCommonData = async () => {
        const custumersList = await getCustumersList();
        setCustumersList(custumersList);
        const colorData = await getColorPlate();
        setColorlist(colorData);
    }

    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'date', header: 'Date', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment ID', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'proformaInvoiceNo', header: 'Proforma Inv.', size: 50, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: 'customerName', header: 'Customer Name', size: 50, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },
            {
                accessorKey: "country",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.country}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'totalAmount', header: 'Amount', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'salesContractNo', header: 'Sales Contract', size: 50, },

        ],
        [permission]
    );

    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };
            const response = await genericPost(GET_EXPORT_PROFORM_REPORT, payload, {});
            if (response.data.success) {
                const proformaInvoices = response.data.data.map((invoice, index) => {
                    const particulars = invoice.particulars.map((particular) => ({
                        qty: particular.qty || 0,
                        rate: parseFloat(particular.rate) || 0,
                        amount: (particular.qty || 0) * (parseFloat(particular.rate) || 0),
                    }));
                    const totalAmount = particulars.reduce((total, { amount }) => total + amount, 0);

                    const countryColorObj = getColorById(invoice.customer.billingCountry.id, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });
                    return {
                        salesContractId: invoice.salesContractId || '',
                        index: (index + 1).toString().padStart(2, '0'),
                        userId: invoice.userId || '',
                        companyId: invoice.companyId || '',
                        shipmentId: invoice.shipmentId || '',
                        customerName: invoice.customer.clientCompanyName || '',
                        country: invoice.customer.billingCountry.name || '',
                        reference: invoice.reference || '',
                        proformaInvoiceNo: invoice?.ProformaInvExp?.proformaInvoiceNo || '',
                        date: companyDateFormat(invoice?.date) || '',
                        dueDate: companyDateFormat(invoice?.dueDate) || '',
                        salesContractNo: invoice.salesContractNo || '',
                        note: invoice.note || '',
                        createdAt: invoice.createdAt || '',
                        updatedAt: invoice.updatedAt || '',
                        particulars,
                        totalAmount: `${invoice?.currency?.currency_symbol || ''} ${totalAmount.toFixed(2)}`,
                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',

                    };
                });
                setData(proformaInvoices);
                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setError('No proforma invoices found');
            }
        } catch (error) {
            setError('Error fetching proforma invoices');
            console.error('Error fetching proforma invoices', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(page, pageSize)
    }, [token, page, pageSize, colorlist])


    const defaultView = 'proforminvreport';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                openDrawer={openDrawer}
                onClose={filterClose}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}

            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={custumersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Customer"
                                        value={custumersList.find((item) => item.id === filterPayload.customerNamesId) || null}
                                        onChange={(event, newValue) => handleFilterChange("customerNamesId", newValue?.id)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );
    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };
    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)}
            />
            {!permission?.view && <AccessDenied />}

        </>
    );
};
export default ProformInvReport;
